import {
  generateBackground,
  type BackgroundRequest,
  type GeneratedBackground,
  type GenerateBackgroundOptions,
} from "./BackgroundGenerator.js";
import { pickFallbackBackground } from "./fallbackContent.js";

/**
 * Regeneration helpers for the character creator (L06).
 *
 * Background regeneration asks the active LLM client; on `null` (no client,
 * quota, malformed replies after retries) it falls back to the offline L05
 * pool. Portraits are no longer regenerated — the player picks from the
 * static pool; see `portraitPool.ts`.
 */

export type RegenerationSource = "llm" | "fallback";

export interface RegeneratedBackground {
  readonly background: GeneratedBackground;
  readonly source: RegenerationSource;
}

export interface RegenerationDeps {
  readonly generateBackground?: (
    req: BackgroundRequest,
    opts?: GenerateBackgroundOptions,
  ) => Promise<GeneratedBackground | null>;
}

export async function regenerateBackground(
  request: BackgroundRequest,
  deps: RegenerationDeps = {},
): Promise<RegeneratedBackground> {
  const gen = deps.generateBackground ?? generateBackground;
  try {
    const bg = await gen(request);
    if (bg) return { background: bg, source: "llm" };
  } catch {
    // Fall through to offline pool.
  }
  return {
    background: pickFallbackBackground({
      race: request.race,
      characterClass: request.characterClass,
    }),
    source: "fallback",
  };
}
